import React, { useContext, useState } from 'react'
import { Alert, Button, Col, Row } from 'react-bootstrap'
import { Controller, useForm } from 'react-hook-form'

import * as FormWizardStyles from '../../../../components/organisms/FormWizard/styles'
import { FormWizardHandlers } from '../../../../components/organisms/FormWizard'
import { CreateContractsContext } from '../index'
import SwitchButton from '../../../../components/Switch'

import { Contract } from '../../../../domain/usecases/Contracts'
import { InputFile } from '../../../../components/molecules/InputFile'
import { yupResolver } from '@hookform/resolvers/yup'
import step7b_schema from './step7b.schema'

export type ICreateContractsFormStep7b = Pick<
  Contract.CreateParams,
  'custom_contract'
> & {
  custom_contract_file?: File
  attachment_files?: File[]
}

type Files = {
  file_type_custom_contract?: string
  file_size?: number
}

export default function Step7b({
  nextHandler,
  backHandler
}: FormWizardHandlers) {
  const { data, setData } = useContext(CreateContractsContext)
  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors }
  } = useForm<ICreateContractsFormStep7b & Files>({
    resolver: yupResolver(step7b_schema)
  })

  const [customContractFile, setCustomContractFile] = useState<File | null>()
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([])

  const custom_contract = watch('custom_contract')

  // O modal de confirmação de criação saiu daqui e foi para o Step7c
  // (Revisar Minuta), que agora é o último passo antes de o contrato existir.
  const onClickNext = (formData: ICreateContractsFormStep7b) => {
    setData?.({
      ...data,
      ...formData,
      custom_contract_file: customContractFile as File,
      attachment_files: attachmentFiles
    })
    nextHandler()
  }

  return (
    <>
      <FormWizardStyles.TabContent>
        <Row>
          <Col md={12}>
            <Alert variant="warning">
              Escolha entre utilizar a nossa minuta padrão de compra e venda ou
              enviar a sua própria minuta.
              <br /> Se optar pela sua minuta, um quadro resumo com os dados
              fornecidos será gerado e anexado ao documento para assinaturas.
            </Alert>
            <Controller
              control={control}
              name="custom_contract"
              render={({ field: { onChange, value } }) => (
                <SwitchButton
                  label="Optar por enviar minha minuta personalizada"
                  value={data?.custom_contract}
                  onChange={(event) => onChange(event)}
                />
              )}
            />
          </Col>
        </Row>
        {custom_contract && (
          <>
            <Row>
              <Col md={4}>
                <InputFile
                  btnText={customContractFile?.name || 'Buscar arquivo'}
                  label="Minuta personalizada"
                  accept="application/pdf"
                  onChange={(file) => {
                    setValue('file_type_custom_contract', file?.type, {
                      shouldValidate: true
                    })
                    setValue('file_size', file?.size, {
                      shouldValidate: true
                    })
                    setCustomContractFile(file)
                  }}
                  errorMessage={
                    errors.custom_contract?.message || errors.file_size?.message
                  }
                />
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Alert variant="warning">
                  Por favor, garanta que todas as lacunas da sua minuta foram
                  devidamente preenchidas
                </Alert>
              </Col>
            </Row>
          </>
        )}

        <Row>
          <Col md={12}>
            <Alert variant="info">
              <strong>Anexos adicionais</strong>
              <br />
              Você pode anexar documentos complementares em PDF que serão
              incluídos no final do contrato. Todos os anexos serão mesclados na
              ordem especificada.
            </Alert>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <InputFile
              btnText="Adicionar Anexo PDF"
              label="Documentos anexos (opcional)"
              accept="application/pdf"
              onChange={(file) => {
                if (file) {
                  setAttachmentFiles((prev) => [...prev, file])
                }
              }}
            />
          </Col>
        </Row>

        {attachmentFiles.length > 0 && (
          <Row>
            <Col md={12}>
              <div className="mt-3">
                <strong>Anexos selecionados:</strong>
                <ul className="list-group mt-2">
                  {attachmentFiles.map((file, index) => (
                    <li
                      key={index}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      <span>{file.name}</span>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => {
                          setAttachmentFiles((prev) =>
                            prev.filter((_, i) => i !== index)
                          )
                        }}
                      >
                        Remover
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            </Col>
          </Row>
        )}

        <Row>
          <Col>
            <FormWizardStyles.ButtonsWrapperSpaced>
              <Button variant="link" onClick={backHandler}>
                Voltar
              </Button>
              <Button variant="primary" onClick={handleSubmit(onClickNext)}>
                Próximo
              </Button>
            </FormWizardStyles.ButtonsWrapperSpaced>
          </Col>
        </Row>
      </FormWizardStyles.TabContent>
    </>
  )
}
